import * as React from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ToastItem {
    id: string;
    title?: string;
    description?: string;
    variant?: 'default' | 'destructive' | 'success';
    duration?: number;
}

type Listener = (item: ToastItem) => void;
const listeners = new Set<Listener>();

export function showToast(item: Omit<ToastItem, 'id'>) {
    const full: ToastItem = { id: crypto.randomUUID(), duration: 3500, ...item };
    listeners.forEach((l) => l(full));
}

export function Toaster() {
    const [toasts, setToasts] = React.useState<ToastItem[]>([]);

    React.useEffect(() => {
        const listener: Listener = (t) => {
            setToasts((arr) => [...arr, t]);
        };
        listeners.add(listener);
        return () => { listeners.delete(listener); };
    }, []);

    const remove = (id: string) => setToasts((arr) => arr.filter((t) => t.id !== id));

    return (
        <ToastPrimitive.Provider swipeDirection="right">
            {toasts.map((t) => (
                <ToastPrimitive.Root
                    key={t.id}
                    duration={t.duration}
                    onOpenChange={(open) => { if (!open) remove(t.id); }}
                    className={cn(
                        'pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden rounded-2xl px-4 py-3 shadow-lg data-[state=open]:animate-fade-in',
                        t.variant === 'destructive' ? 'bg-red-50 border border-red-200 text-red-900'
                        : t.variant === 'success' ? 'bg-green-50 border border-green-200 text-green-900'
                        : 'bg-white border border-gray-200 text-gray-900',
                    )}
                >
                    <div className="flex-1 min-w-0">
                        {t.title && <ToastPrimitive.Title className="text-sm font-bold">{t.title}</ToastPrimitive.Title>}
                        {t.description && <ToastPrimitive.Description className="text-xs">{t.description}</ToastPrimitive.Description>}
                    </div>
                    <ToastPrimitive.Close className="text-gray-400 hover:text-gray-600">
                        <X className="h-4 w-4" />
                    </ToastPrimitive.Close>
                </ToastPrimitive.Root>
            ))}
            <ToastPrimitive.Viewport className="fixed top-0 right-0 z-[100] flex w-full max-w-sm flex-col gap-2 p-4 pt-safe outline-none" />
        </ToastPrimitive.Provider>
    );
}
